/**
 * Shared asset sub-route configs.
 *
 * These are relative-path route entries that are mounted under both:
 *   - `/assets/:assetId/...`          (from assets/routes.tsx)
 *   - `/activities/:issueId/asset/:assetId/...`  (from activity/routes.tsx)
 *
 * Each entry carries `handle.panel` so PanelShell's `transitionKey` updates
 * whenever the user navigates between sub-routes, driving the slide-in animation.
 *
 * CLAUDE.md rule: assets/routes.tsx and activity/routes.tsx changes are
 * additive — existing flat entries are preserved for legacy URL compatibility.
 */
import { PermissionValues as Permissions } from '@gripp/shared-logic';
import { ActivityEdit } from '../activity/components/activity-edit';
import { ActivityReassign } from '../activity/components/activity-reassign';
import { ActivityRevise } from '../activity/components/activity-revise';
import { AssetAlertFormPage } from './components/asset-alert-form-page';
import { AssetRoutineAlerts } from './components/asset-routine-alerts';
import { AssetRoutineSubmit } from './components/asset-routine-submit';
import { AssetRoutines } from './components/asset-routines';
import { AssetRoutinesAll } from './components/asset-routines-all';
import { AssetRoutinesRearrange } from './components/asset-routines-rearrange';
import { AssetActivity } from './components/assetActivity';
import { AssetDetail } from './components/assetDetail';
import { AssetDetailFilter } from './components/assetDetailFilter';
import { AssetFileEdit } from './components/assetFileEdit';
import { AssetFilePreview } from './components/assetFilePreview';
import { AssetFiles } from './components/assetFiles';
import { AssetNewIssue } from './components/assetNewIssue';
import { EditAsset } from './components/edit';

/**
 * Relative-path route configs for all sub-routes under an asset.
 * Mount under `<parentPath>/:assetId/` in the route tree.
 */
export const assetSubRoutes = [
  {
    path: 'edit',
    element: <EditAsset />,
    handle: {
      panel: { key: 'asset-edit', title: 'topNav.editAsset' },
      header: { titleKey: 'topNav.editAsset', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'activity/new',
    element: <AssetNewIssue />,
    handle: {
      panel: { key: 'asset-activity-new', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.issue.create,
    },
  },
  {
    path: 'activity/all',
    element: <AssetDetail />,
    handle: {
      panel: { key: 'asset-activity-all', title: 'topNav.assetDetail' },
      header: { titleKey: 'topNav.assetDetail', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'activity/filters',
    element: <AssetDetailFilter />,
    handle: {
      panel: { key: 'asset-activity-filters', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'activity/:issueId',
    element: <AssetActivity />,
    handle: {
      panel: { key: 'asset-activity', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'activity/:issueId/edit',
    element: <ActivityEdit />,
    handle: {
      panel: { key: 'asset-activity-edit', title: 'activity.menu.title' },
      header: { titleKey: 'activity.menu.title', icon: 'bell-outline' },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: 'activity/:issueId/edit/reassign',
    element: <ActivityReassign />,
    handle: {
      panel: { key: 'asset-activity-edit-reassign', title: 'activity.menu.title' },
      header: { titleKey: 'activity.menu.title', icon: 'bell-outline' },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: 'activity/:issueId/edit/revise',
    element: <ActivityRevise />,
    handle: {
      panel: { key: 'asset-activity-edit-revise', title: 'activity.menu.title' },
      header: { titleKey: 'activity.menu.title', icon: 'bell-outline' },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: 'files/all',
    element: <AssetFiles />,
    handle: {
      panel: { key: 'asset-files', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'files/:mediaId',
    element: <AssetFilePreview />,
    handle: {
      panel: { key: 'asset-file-preview', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'files/:mediaId/edit',
    element: <AssetFileEdit />,
    handle: {
      panel: { key: 'asset-file-edit', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: 'routines',
    element: <AssetRoutines />,
    handle: {
      panel: { key: 'asset-routines', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/all',
    element: <AssetRoutinesAll />,
    handle: {
      panel: { key: 'asset-routines-all', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/edit',
    element: <AssetRoutinesRearrange />,
    handle: {
      panel: { key: 'asset-routines-edit', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: 'routines/:routineId',
    element: <AssetRoutineSubmit />,
    handle: {
      panel: { key: 'asset-routine-submit', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/all/:routineId',
    element: <AssetRoutineSubmit />,
    handle: {
      panel: { key: 'asset-routine-submit-all', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/:routineId/alerts',
    element: <AssetRoutineAlerts />,
    handle: {
      panel: { key: 'asset-routine-alerts', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/:routineId/alerts/new',
    element: <AssetAlertFormPage />,
    handle: {
      panel: { key: 'asset-routine-alert-new', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/:routineId/alerts/:alertId/edit',
    element: <AssetAlertFormPage />,
    handle: {
      panel: { key: 'asset-routine-alert-edit', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
];
